import { useEffect, useState } from "react";
import BrandMark from "@/components/BrandMark";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ScrollText, Shield, FileCheck, Building2, Sparkles,
  Scale, ShieldCheck, Timer, LockKeyhole, Zap, MoveUpRight, Globe2, MessageSquare,
} from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { API } from "@/lib/apiClient";
import SiteFooter from "@/components/SiteFooter";
import SignInDialog from "@/components/SignInDialog";
import { useAuth } from "@/contexts/AuthContext";
import { homePathFor } from "@/lib/roles";
import { STAMP_IMG } from "@/components/PDFPreviews";

const affidavitTypes = 15;
// No `to` on these. The cards are a shop window: they say what is offered and what it costs, and
// signing in is the one way through. Three of the four used to point straight at a wizard, which a
// signed-out visitor cannot use, and the fourth at a public catalogue page that existed only to
// give one of them somewhere to go. Prices come from GET /pricing, so they follow Admin > Pricing
// without an edit here.
const buildServices = (p) => ([
  { key: "rental", icon: ScrollText, title: "Rental & Lease", price: p?.rental_agreement?.total,
    desc: "Tamil Nadu-compliant residential & commercial agreements. Live preview on genuine registered stamp paper." },
  { key: "aff", icon: FileCheck, title: `Affidavits · ${affidavitTypes}+`, price: p?.affidavit?.total,
    desc: "Change of Name, Address Proof, Marriage, Domicile — guided step-by-step." },
  { key: "contract", icon: Shield, title: "Contract Review", price: p?.contract_review?.total,
    desc: "Advocate-led risk assessment — clauses, liability, compliance flags in 24h." },
  { key: "prop", icon: Building2, title: "Property Verification", price: p?.property_verification?.total,
    desc: "Title, Encumbrance, POA & Litigation checks. Built for buyers and NRIs." },
]);

// Say what the system does, and nothing beyond it. Every label here has to be defensible if a
// customer asks how — these are security and service claims made to people handing over property
// details and identity documents, and the page is not the place to round them up.
//
// "Encrypted end-to-end" used to sit in the last slot and was simply untrue: there is no
// encryption anywhere in the backend (no crypto library is even installed), uploads are plain
// files on disk and `form_data` is plain text in the database. End-to-end also means something
// specific — that the *server* cannot read the data — which is the opposite of how this product
// works, since admin and the reviewing advocate both must. What is actually true is TLS between
// the browser and the server, which is what the label now says. Same mistake as the "AI
// drafting…" copy that was corrected in the hero.
const trustPoints = [
  // "TN stamp-duty aware" claimed a calculation this system does not perform: there is no
  // stamp-duty logic anywhere in the backend, only a flat `stamp_paper_cost` per document type
  // that an admin sets by hand in Pricing. Actual duty varies with rent, tenure and property
  // value. What is true is that the paper is supplied and the document is delivered on it.
  { icon: Scale, label: "TN stamp paper included" },
  { icon: ShieldCheck, label: "Bar-registered advocates" },
  { icon: Timer, label: "24 hour turnaround" },
  { icon: LockKeyhole, label: "Encrypted in transit" },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Landing() {
  const [cfg, setCfg] = useState({ whatsapp: "919876543210", support_email: "hello@dharmatn.in" });
  const [pricing, setPricing] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  // This page is reachable while signed in — the footer's disclaimer link comes back through
  // here, and App.js's catch-all route redirects here. It used to have no signed-in state at
  // all, so its only control was a customer Sign In button: an admin who arrived this way was
  // shown a login prompt with no route back to their own portal, which is indistinguishable
  // from having been signed out (the session is untouched).
  const { user } = useAuth();
  const home = homePathFor(user?.role);
  const services = buildServices(pricing);
  const prefersReduced = useReducedMotion();
  useEffect(() => {
    fetch(`${API}/site-config`)
      .then(r => r.json()).then(setCfg).catch(() => {});
    fetch(`${API}/pricing`)
      .then(r => r.json()).then(setPricing).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#080C16] text-[#F9F8F6] antialiased selection:bg-[#CBA96C]/30">
      {/* Ambient glow layers */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full blur-3xl opacity-25"
             style={{ background: "radial-gradient(circle, rgba(203,169,108,0.5), transparent 55%)" }} />
        <div className="absolute bottom-[-30%] right-[-10%] w-[900px] h-[900px] rounded-full blur-3xl opacity-15"
             style={{ background: "radial-gradient(circle, rgba(88,101,242,0.5), transparent 60%)" }} />
      </div>

      {/* NAV */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#080C16]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 font-serif-display text-lg tracking-tight">
            <BrandMark /> DharmaTN
          </Link>
          <nav className="hidden md:flex gap-8 text-sm text-[#A0AABF]">
            <a href="#nri" data-testid="nav-nri" className="hover:text-white transition-colors">For NRIs</a>
            <a href="#services" data-testid="nav-services" className="hover:text-white transition-colors">Services</a>
            <a href="#how" data-testid="nav-how" className="hover:text-white transition-colors">How it works</a>
            <a href="#trust" data-testid="nav-trust" className="hover:text-white transition-colors">Why us</a>
          </nav>
          <div className="flex items-center gap-2">
            {/* One CTA only: "Sign In" and "Get Started" both landed on an auth page. It opens the
                sign-in card over this page rather than navigating to /login, so closing it puts the
                visitor back exactly where they were. */}
            {user ? (
              <Link to={home} data-testid="nav-portal">
                <Button className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] rounded-full h-9 px-5 font-medium">
                  Go to dashboard
                </Button>
              </Link>
            ) : (
              <Button
                onClick={() => setAuthOpen(true)} data-testid="nav-login"
                className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] rounded-full h-9 px-5 font-medium"
              >Sign In</Button>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        {/* Hero rhythm is tuned so the "Live preview" block clears the fold on a 1366x768
            laptop (~600px of viewport), not just on a large monitor. The previous
            pt-24/md:pt-32 pushed it to ~684px from the top, which read as an empty screen on
            the most common visitor resolution. Treat these numbers as a budget: anything added
            to this section pushes the preview back below the fold. */}
        <div className="max-w-7xl mx-auto px-6 pt-12 md:pt-16 pb-16 md:pb-24">
          <motion.div {...fadeUp(0)} className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#CBA96C] mb-6">
            <Sparkles className="w-3 h-3" /> Legal Document Studio · Tamil Nadu
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tighter max-w-4xl"
          >
            The future of<br />legal in Tamil Nadu <span className="italic text-[#CBA96C]">— drafted in minutes.</span>
          </motion.h1>
          <motion.p {...fadeUp(0.15)} className="mt-6 text-lg md:text-xl text-[#A0AABF] max-w-2xl leading-relaxed">
            Guided document drafting with live preview on genuine non-judicial stamp paper.
            Reviewed by bar-registered advocates. Delivered to your door.
          </motion.p>

          {/* No hero CTA buttons on purpose. "Draft a Rental Agreement" and "Explore all
              services" both restated what the services list below already offers — one of them
              linked to a service that has its own entry there, the other simply scrolled down.
              Two buttons that duplicate the section beneath them add a decision without adding a
              destination. */}

          {/* Live doc preview card */}
          <motion.div {...fadeUp(0.35)} className="mt-12 md:mt-14 grid md:grid-cols-[1fr_minmax(420px,520px)] gap-10 items-center">
            <div className="space-y-5">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#A0AABF]">Live preview</div>
              <h3 className="font-serif-display text-3xl md:text-4xl leading-tight">Watch your document take shape,<br /> clause by clause.</h3>
              <p className="text-[#A0AABF] leading-relaxed max-w-md">
                Every keystroke is rendered onto authentic Tamil Nadu registered stamp paper. Edit anything,
                anytime, until you&rsquo;re ready to print.
              </p>
              <div className="flex flex-wrap gap-2 pt-2">
                {/* Facts about the service, not invented statistics.
                    "12,400+ documents" and "98% satisfaction" were hard-coded strings — nothing
                    computed them, and the platform has not launched, so no documents have been
                    drafted and nobody has expressed satisfaction. Presenting fabricated usage
                    figures to customers is the same problem as the "Encrypted end-to-end" claim
                    corrected below, and the easiest of the two to disprove.
                    If these should show real numbers later, wire them to GET /admin/analytics so
                    they become true automatically rather than being retyped. */}
                <PillStat label="stamp paper" value="Included" />
                <PillStat label="reviewed by" value="Advocate" />
                <PillStat label="turnaround" value="24 h" />
              </div>
            </div>
            <div className="relative">
              {!prefersReduced && (
                <motion.div
                  initial={{ opacity: 0, y: 30, rotate: -2 }} animate={{ opacity: 1, y: 0, rotate: -2 }}
                  transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="relative"
                >
                  <div className="absolute -inset-4 rounded-3xl blur-2xl opacity-40"
                       style={{ background: "linear-gradient(120deg, #CBA96C 0%, transparent 60%)" }} />
                  <div className="pdf-page relative -rotate-2">
                    {/* Same locally-served sheet the real previews use — see STAMP_IMG in
                        components/PDFPreviews.jsx. */}
                    <img src={STAMP_IMG} alt="Tamil Nadu registered stamp paper" className="stamp-header" />
                    <div className="pdf-body">
                      <h1>RENTAL AGREEMENT</h1>
                      <p>This Agreement is made on ____________ between <b>Landlord</b>, residing at __________, hereinafter the <b>LANDLORD</b>…</p>
                      <p>And <b>Tenant</b>, hereinafter the <b>TENANT</b>. Both parties agree to the terms for the premises at <b>[Property Address]</b>…</p>
                    </div>
                    <div className="sig-row"><span>LANDLORD</span><span>TENANT</span></div>
                  </div>
                </motion.div>
              )}
              {/* "Live preview" badge floating over the sheet. Says what the product actually does
                  — the wizard re-renders the document on every keystroke. It previously read "AI
                  drafting…", which claimed a capability this system does not have: documents are
                  produced by token substitution into advocate-authored templates, not generated. */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.1, duration: 0.4 }}
                className="absolute -bottom-4 -left-2 md:-left-6 flex items-center gap-2 bg-[#111623] border border-white/10 rounded-full px-4 py-2 shadow-[0_10px_40px_rgba(0,0,0,0.5)]"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#CBA96C] opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#CBA96C]" />
                </span>
                <span className="text-xs text-[#F9F8F6]">Live preview</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOR NRIs — straight after the hero rather than inside it: the hero has a fold budget (see
          the note above), and this is the first thing a visitor reaches on scrolling. Every line
          here has to be true of the product as built, like the trust points: the advocate really
          is on the case's message thread (one shared thread — see backend/CLAUDE.md "Case
          messages"), and sign-in really is Google/Facebook from anywhere. Nothing here promises
          international payments, video calls or overseas delivery, which the platform does not do. */}
      <section id="nri" className="border-t border-white/5 bg-[#0B111C]">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <motion.div {...fadeUp(0)}>
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#CBA96C] mb-4">
              <Globe2 className="w-3 h-3" /> For NRIs · Tamil Nadu
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Abroad? Your Tamil Nadu advocate is <span className="italic text-[#CBA96C]">one message away.</span>
            </h2>
            <p className="mt-6 text-lg text-[#A0AABF] leading-relaxed max-w-xl" data-testid="nri-lead">
              Direct contact with bar-registered Tamil Nadu advocates for NRIs, for Contract Review and
              Property Verification. You don&rsquo;t need to fly home or rely on a relative to chase it for you.
            </p>
            <ul className="mt-8 space-y-4 text-sm text-[#D6DCE8]">
              {[
                { icon: MessageSquare, t: "Talk to your advocate directly", d: "Every case has its own message thread with the advocate handling it, open from wherever you are." },
                { icon: Globe2, t: "Sign in from any country", d: "Use your Google or Facebook account. No Indian mobile OTP needed to get started." },
                { icon: ShieldCheck, t: "Bar-registered advocates", d: "Contract and property reviews are carried out by bar-registered Tamil Nadu advocates." },
              ].map(p => (
                <li key={p.t} className="flex gap-3">
                  <p.icon className="w-5 h-5 text-[#CBA96C] shrink-0 mt-0.5" strokeWidth={1.5} />
                  <span><b className="text-white font-medium">{p.t}.</b> <span className="text-[#A0AABF]">{p.d}</span></span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex gap-3 flex-wrap">
              {user ? (
                <Link to={home} data-testid="nri-portal">
                  <Button className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] h-11 px-6 rounded-full font-medium">
                    Go to dashboard <MoveUpRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              ) : (
                <Button onClick={() => setAuthOpen(true)} data-testid="nri-signin"
                        className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] h-11 px-6 rounded-full font-medium">
                  Get started <MoveUpRight className="ml-2 w-4 h-4" />
                </Button>
              )}
              <a href={`https://wa.me/${cfg.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="nri-whatsapp">
                <Button variant="outline" className="h-11 px-6 rounded-full bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white">
                  WhatsApp us from abroad
                </Button>
              </a>
            </div>
          </motion.div>

          <div className="grid gap-5">
            {[
              { key: "contract", icon: Shield, title: "Contract Review", price: pricing?.contract_review?.total,
                d: "Send the contract you are about to sign. An advocate reads it and flags the risk, liability and compliance issues, clause by clause." },
              { key: "prop", icon: Building2, title: "Property Verification", price: pricing?.property_verification?.total,
                d: "Title, encumbrance, power-of-attorney and litigation checks on Tamil Nadu property, before you buy, sell or sign one from abroad." },
            ].map((c, i) => (
              <motion.div key={c.key} {...fadeUp(0.08 * (i + 1))} data-testid={`nri-card-${c.key}`}
                className="bg-[#111623] border border-white/10 rounded-2xl p-7 hover:border-[#CBA96C]/40 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <c.icon className="w-7 h-7 text-[#CBA96C]" strokeWidth={1.5} />
                  <span className="text-right leading-tight">
                    <span className="block text-[10px] tracking-[0.24em] uppercase text-[#A0AABF]">from</span>
                    <b className="font-serif-display text-white text-lg">{c.price != null ? `₹${Math.round(c.price)}` : "—"}</b>
                  </span>
                </div>
                <h3 className="mt-5 font-serif-display text-2xl tracking-tight">{c.title}</h3>
                <p className="mt-2 text-sm text-[#A0AABF] leading-relaxed">{c.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES — Bento */}
      <section id="services" className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">
          <motion.div {...fadeUp(0)} className="mb-14 max-w-2xl">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#CBA96C] mb-3">Four workflows · one platform</div>
            <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight">
              Every legal moment, <span className="italic text-[#CBA96C]">designed</span>.
            </h2>
          </motion.div>

          {/* Four equal vertical cards, not a bento.
              The original layout gave Rental & Lease a card four times the size of the others and
              rendered the last two `compact`, which dropped their descriptions entirely — so two
              of the four services could not say what they were. Sizing was standing in for
              importance without anyone having decided they were less important.

              Breakpoints: 4-up only from xl (1280px), where each card is ~293px — a portrait
              shape. At lg it would be ~229px, which squeezes the title onto three lines, so the
              step down to 2-up happens before that rather than after. A fifth service is one
              array entry; the grid does not need rethinking. */}
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
            {services.map((s, i) => <ServiceCard key={s.key} s={s} index={i} />)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">
          <motion.div {...fadeUp(0)} className="mb-14 max-w-2xl">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#CBA96C] mb-3">Method</div>
            <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight">Three steps. Minutes, not hours.</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { n: "01", t: "Select & fill", d: "Choose a document, answer a guided questionnaire — our system customises it around your answers." },
              { n: "02", t: "Preview live", d: "Watch your document take shape on authentic non-judicial stamp paper. Edit anytime before submission." },
              { n: "03", t: "Print or deliver", d: "Download and print yourself — or opt for doorstep delivery on genuine stamp paper." },
            ].map((s, i) => (
              <motion.div key={s.n} {...fadeUp(0.05 * i)}
                className="bg-[#111623] border border-white/10 rounded-2xl p-8 hover:border-white/20 transition-colors">
                <div className="font-serif-display text-5xl text-[#CBA96C]">{s.n}</div>
                <div className="mt-4 font-serif-display text-2xl">{s.t}</div>
                <p className="mt-3 text-sm text-[#A0AABF] leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section id="trust" className="border-t border-white/5 bg-[#0B111C]">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <motion.div {...fadeUp(0)} className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div className="max-w-xl">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#CBA96C] mb-3">Built for confidence</div>
              <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight">Trusted where it matters.</h2>
            </div>
            <p className="text-[#A0AABF] max-w-md">
              DharmaTN pairs modern intelligence with human review. Every document is verified
              before delivery.
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {trustPoints.map((p, i) => (
              <motion.div key={p.label} {...fadeUp(0.04 * i)}
                className="bg-[#111623] border border-white/10 rounded-xl px-5 py-6 flex items-center gap-3">
                <p.icon className="w-5 h-5 text-[#CBA96C]" strokeWidth={1.5} />
                <span className="text-sm">{p.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <motion.div {...fadeUp(0)}>
            <Zap className="w-6 h-6 text-[#CBA96C] mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="font-serif-display text-4xl md:text-6xl tracking-tighter leading-tight">
              Legal, at the speed <br /><span className="italic text-[#CBA96C]">of thought.</span>
            </h2>
            <div className="mt-10 flex justify-center gap-3 flex-wrap">
              {user ? (
                <Link to={home} data-testid="cta-portal">
                  <Button className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] hover:shadow-[0_0_28px_rgba(203,169,108,0.35)] h-12 px-7 rounded-full font-medium">
                    Go to dashboard <MoveUpRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              ) : (
                <Button
                  onClick={() => setAuthOpen(true)} data-testid="cta-signup"
                  className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] hover:shadow-[0_0_28px_rgba(203,169,108,0.35)] h-12 px-7 rounded-full font-medium"
                >
                  Get started free <MoveUpRight className="ml-2 w-4 h-4" />
                </Button>
              )}
              <a href={`https://wa.me/${cfg.whatsapp}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="h-12 px-7 rounded-full bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white">
                  Talk to us on WhatsApp
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-10 text-xs text-[#6B7280] leading-relaxed text-center">
          {/* The platform connects; the advocate advises. Saying DharmaTN "provides no legal advice"
              beside a Contract Review card read as a contradiction, so the connection is named and
              the opinion is attributed to the advocate who gives it, not to the platform. */}
          DharmaTN.com does not itself provide legal advice, consultation, accounting or auditing services. It is neither a Law Firm nor a Chartered Accountancy or Company Secretary Firm. For Contract Review and Property Verification, DharmaTN.com connects you with independent advocates enrolled with the Bar Council in Tamil Nadu; any legal opinion in that review is given by the advocate, not by DharmaTN.com. Our other services should not be construed as, or taken to be, a substitute or alternative to legal advice — <Link to="/disclaimer" data-testid="disclaimer-link" className="underline text-[#A0AABF] hover:text-white">Read complete disclaimer</Link>.
        </div>
      </section>

      {/* The same footer every other page now carries — it was written out here, which is why it
          vanished the moment anyone signed in. */}
      <SiteFooter className="mt-0" />

      <SignInDialog open={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
}

function PillStat({ label, value }) {
  return (
    <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5">
      <span className="font-serif-display text-base text-white">{value}</span>
      <span className="text-xs text-[#A0AABF]">{label}</span>
    </div>
  );
}

function ServiceCard({ s, index }) {
  const Icon = s.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.5, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
      /* h-full is what makes the four cards one height: CSS grid stretches the row, and without
         it each card would be as tall as its own description, leaving a ragged bottom edge that
         reads as broken rather than as deliberate. */
      className="h-full rounded-2xl border border-white/10 bg-[#111623]"
    >
      {/* Display only — these do not link anywhere.
          Three of the four services had no destination of their own, so only Affidavits actually
          went somewhere and the rest either dead-ended or dropped a signed-out visitor into a
          wizard. A row of cards where one is clickable and three are not is worse than a row where
          none are: people learn the pattern from the first card they try. Signing in is the single
          way into the product, and the header's Sign In button is where that lives. */}
      <div data-testid={`service-card-${s.key}`} className="flex h-full flex-col p-7">
        <Icon className="w-8 h-8 text-[#CBA96C]" strokeWidth={1.5} />

        <h3 className="mt-6 font-serif-display text-xl md:text-2xl tracking-tight">{s.title}</h3>

        <p className="mt-3 text-sm text-[#A0AABF] leading-relaxed">{s.desc}</p>

        {/* mt-auto pins the price and the arrow to the bottom of every card, so they line up
           across the row however long each description runs. */}
        <div className="mt-auto pt-8 flex items-end justify-between">
          <span className="leading-tight">
            <span className="block text-[10px] tracking-[0.24em] uppercase text-[#A0AABF]">from</span>
            <b className="font-serif-display text-white text-lg">
              {s.price != null ? `₹${Math.round(s.price)}` : "—"}
            </b>
          </span>
          {/* aria-hidden: it repeats the link rather than adding to it. */}
        </div>
      </div>
    </motion.div>
  );
}
